import React, { useState, useEffect } from 'react';
import { Form, Tabs, Tab, Spinner, Card, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setFormData, startTask, updateTask } from '../../store/taskSlice';
import { flaskServiceAPI, claimsAPI, usersAPI } from '../../services/apiService';
import { registerForTask } from '../../services/socketService';
import { message } from 'antd';
import { RocketOutlined, LockOutlined } from '@ant-design/icons';
import { getTranslatedError } from '../../utils/errorTranslator';
import ImageSearchTab from './ImageSearchTab';
import TextSearchTab from './TextSearchTab';
import ScreenshotTab from './ScreenshotTab';

/** Обёртка контента вкладки: запускает анимацию появления после того, как вкладка станет видимой (через rAF). */
const TabContentAnimated = ({ isActive, children }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setAnimate(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimate(true));
    });
    return () => cancelAnimationFrame(id);
  }, [isActive]);

  return (
    <div className={`tab-pane-inner${animate ? ' tab-pane-inner-visible' : ''}`}>
      {children}
    </div>
  );
};

const TaskForm = ({ onGenerationSuccess }) => {
  const dispatch = useDispatch();
  const { formData, status } = useSelector((state) => state.task);
  const [files, setFiles] = useState([]);
  const [user, setUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isBusy = isSubmitting || status === 'processing';

  useEffect(() => {
    // Получаем информацию о пользователе из localStorage или API
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    
    // Также можно запросить актуальные данные
    const fetchProfile = async () => {
      try {
        const data = await usersAPI.getProfile();
        console.log('Fetched user profile:', data); // Debug log
        // API может возвращать объект пользователя или обертку { user: ... }
        const profile = data.user || data;
        setUser(profile);
        // Обновляем localStorage, чтобы данные были актуальными
        localStorage.setItem('user', JSON.stringify(profile));
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFormData({ [name]: value }));
  };

  const handlePaste = (e) => {
    if (formData.searchType !== 'screenshot') return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          // Проверка на дубликаты по имени, размеру и дате
          const isDuplicate = files.some(
            existing =>
              existing.file.name === file.name &&
              existing.file.size === file.size &&
              existing.file.lastModified === file.lastModified
          );
          if (isDuplicate) continue;
          if (files.length >= 10) {
            message.error('Можно загрузить не более 10 файлов');
            return;
          }
          setFiles(prev => [...prev, { file, previewUrl: URL.createObjectURL(file) }]);
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  });

  // Проверяем активность пользователя (поддержка обоих форматов: isActive и is_active)
  const isUserInactive = user && (user.isActive === false || user.is_active === false);

  if (isUserInactive) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4 text-center">
          <div className="mb-3">
            <LockOutlined style={{ fontSize: '48px', color: '#faad14' }} />
          </div>
          <Card.Title className="mb-3">Аккаунт не активен</Card.Title>
          <Card.Text className="text-muted">
            Ваш аккаунт ожидает активации. Вы не можете создавать новые протоколы, пока администратор не активирует вашу учетную запись.
          </Card.Text>
          <Alert variant="warning" className="d-inline-block mt-2">
            Пожалуйста, подтвердите номер телефона или свяжитесь с администратором для активации аккаунта.
          </Alert>
          <div className="mt-3">
             <p className="text-muted small">
               Вы по-прежнему можете пользоваться личным кабинетом и отправлять сообщения администраторам.
             </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const handleFilesChange = ({ fileList }) => {
    if (fileList.length > 10) {
      message.error('Можно загрузить не более 10 файлов');
      return;
    }
    // fileList может содержать дубликаты, уберём их по имени+размеру+lastModified
    const unique = [];
    const seen = new Set();
    for (const f of fileList) {
      const file = f.originFileObj;
      const key = `${file.name}_${file.size}_${file.lastModified}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push({
          file,
          previewUrl: f.thumbUrl || (file ? URL.createObjectURL(file) : null)
        });
      }
    }
    setFiles(unique);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleTabChange = (searchType) => {
    const newData = {
      searchType,
      url: formData.url
    };
    
    if (searchType === 'image') {
      newData.text = '';
    } else if (searchType === 'text') {
      newData.imageUrl = '';
    } else if (searchType === 'screenshot') {
      newData.text = '';
      newData.imageUrl = '';
    }
    
    dispatch(setFormData(newData));
  };

  // Функция для нормализации URL - добавляет протокол, если его нет
  const normalizeUrl = (url) => {
    if (!url) return url;
    
    // Убираем пробелы в начале и конце
    url = url.trim();
    
    // Если URL уже начинается с http:// или https://, возвращаем как есть
    if (url.match(/^https?:\/\//i)) {
      return url;
    }
    
    // Если URL не содержит протокол, добавляем https://
    return `https://${url}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isBusy) return;
    
    if (formData.searchType === 'screenshot') {
      if (files.length === 0 || !formData.url) {
        message.error('Необходимо загрузить хотя бы один скриншот и указать URL сайта');
        return;
      }

      setIsSubmitting(true);
      try {
        // Нормализуем URL - добавляем протокол, если его нет
        const normalizedUrl = normalizeUrl(formData.url);
        
        // Создаем заявку через Claims API
        const claimData = {
          url: normalizedUrl,
          searchType: 'screenshot',
          screenshots: files.length
        };
        
        // Сначала создаем заявку через Claims API (если доступен)
        let claimResponse;
        try {
          claimResponse = await claimsAPI.createClaim(claimData);
        } catch (claimError) {
          // Если Claims API недоступен, продолжаем без создания заявки
          console.warn('Claims API недоступен, продолжаем без создания заявки');
          claimResponse = null;
        }

        // Отправляем задачу через бэкенд
        const callbackUrl = claimResponse?.id 
          ? `https://fixator-app.ru/api/v2/claims/${claimResponse.id}/callback`
          : undefined;
        
        // Извлекаем файлы и проверяем их валидность
        const screenshotFiles = files
          .map(f => f.file)
          .filter(file => file != null); // Убираем null/undefined
        
        if (screenshotFiles.length === 0) {
          message.error('Не удалось загрузить файлы скриншотов');
          setIsSubmitting(false);
          return;
        }
        
        const response = await flaskServiceAPI.generateFromScreenshot(
          normalizedUrl,
          screenshotFiles,
          callbackUrl
        );
        
        if (response.task_id) {
          dispatch(startTask({
            ...response,
            claimId: claimResponse?.id || null
          }));
          registerForTask(response.task_id);
          pollTaskStatus(response.task_id);
          onGenerationSuccess?.();
        } else {
          message.error('Ошибка при создании задачи: ' + getTranslatedError(response.error || response, 'Неизвестная ошибка'));
        }
      } catch (error) {
        console.error('Task creation error:', error);
        message.error('Ошибка при создании задачи: ' + getTranslatedError(error, 'Неизвестная ошибка'));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Оригинальная логика для других вкладок
    if (!formData.url) {
      message.error('Необходимо указать URL сайта');
      return;
    }

    if (formData.searchType === 'image' && !formData.imageUrl) {
      message.error('Необходимо указать ссылку на изображение');
      return;
    }

    if (formData.searchType === 'text' && !formData.text) {
      message.error('Необходимо ввести текст для поиска');
      return;
    }

    setIsSubmitting(true);
    try {
      // Нормализуем URL - добавляем протокол, если его нет
      const normalizedUrl = normalizeUrl(formData.url);
      // Нормализуем imageUrl, если он указан
      const normalizedImageUrl = formData.imageUrl ? normalizeUrl(formData.imageUrl) : formData.imageUrl;
      
      // Создаем заявку через Claims API
      const claimData = {
        url: normalizedUrl,
        searchType: formData.searchType,
        imageUrl: normalizedImageUrl,
        text: formData.text
      };
      
      // Сначала создаем заявку через Claims API (если доступен)
      let claimResponse;
      try {
        claimResponse = await claimsAPI.createClaim(claimData);
      } catch (claimError) {
        // Если Claims API недоступен, продолжаем без создания заявки
        console.warn('Claims API недоступен, продолжаем без создания заявки');
        claimResponse = null;
      }

      // Отправляем задачу через бэкенд
      const response = await flaskServiceAPI.generate(
        normalizedUrl,
        normalizedImageUrl,
        formData.text
      );
      
      if (response.task_id) {
        dispatch(startTask({
          ...response,
          claimId: claimResponse?.id || null
        }));
        registerForTask(response.task_id);
        pollTaskStatus(response.task_id);
        onGenerationSuccess?.();
      } else {
        message.error('Ошибка при создании задачи: ' + getTranslatedError(response.error || response, 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('Task creation error:', error);
      message.error('Ошибка при создании задачи: ' + getTranslatedError(error, 'Неизвестная ошибка'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Функция для опроса статуса задачи
  const pollTaskStatus = async (taskId) => {
    const maxAttempts = 120; // Максимум 10 минут (120 * 5 секунд)
    let attempts = 0;

    const poll = async () => {
      try {
        const taskStatus = await flaskServiceAPI.getTaskStatus(taskId);

        const dispatchCompleted = (data) => {
          dispatch(updateTask({
            status: 'completed',
            result: {
              task_id: taskId,
              download_url: data.result?.download_url,
              mhtml_view_url: data.result?.mhtml_view_url,
              mhtml_download_url: data.result?.mhtml_download_url,
              screenshot_url: data.result?.screenshot_url,
              screenshot_urls: data.result?.screenshot_urls,
              mhtml_warnings: data.result?.mhtml_warnings,
              mhtml_error: data.result?.mhtml_error,
              protocol_number: data.protocol_number,
              protocol_date: data.protocol_date
            }
          }));
        };

        if (taskStatus.status === 'completed' || taskStatus.status === 'completed_with_warnings') {
          dispatchCompleted(taskStatus);
        } else if (taskStatus.status === 'failed') {
          dispatch(updateTask({
            status: 'failed',
            error: taskStatus.error || 'Ошибка при обработке задачи'
          }));
        } else if (
          (taskStatus.status === 'processing' || taskStatus.status === 'pending') &&
          attempts < maxAttempts
        ) {
          attempts++;
          setTimeout(poll, 5000);
        } else if (attempts >= maxAttempts) {
          dispatch(updateTask({
            status: 'failed',
            error: 'Превышено время ожидания обработки задачи'
          }));
        }
      } catch (error) {
        console.error('Error polling task status:', error);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 5000);
        } else {
          dispatch(updateTask({
            status: 'failed',
            error: 'Ошибка при проверке статуса задачи'
          }));
        }
      }
    };

    // Начинаем опрос через 5 секунд
    setTimeout(poll, 5000);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold mb-1" style={{ fontSize: '14px', color: '#1d1d1f' }}>
          URL сайта <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          type="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          placeholder="https://example.com"
          required
          className="glass-input"
        />
        <Form.Text className="text-muted mt-0 d-block" style={{ fontSize: '12px' }}>
          Укажите адрес сайта, на котором нужно выполнить поиск
        </Form.Text>
      </Form.Group>

      <div className="mb-3">
        <Form.Label className="fw-semibold mb-1" style={{ fontSize: '14px', color: '#1d1d1f' }}>
          Режим поиска
        </Form.Label>
        <div className="glass-segmented">
          <Tabs
            activeKey={formData.searchType}
            onSelect={handleTabChange}
            transition={false}
            className="mb-0 border-0 create-protocol-tabs"
          >
            <Tab
              eventKey="image"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🖼️</span> По изображению
                </span>
              }
            >
              <TabContentAnimated isActive={formData.searchType === 'image'}>
                <ImageSearchTab formData={formData} handleChange={handleChange} />
              </TabContentAnimated>
            </Tab>
            <Tab
              eventKey="text"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>📝</span> По тексту
                </span>
              }
            >
              <TabContentAnimated isActive={formData.searchType === 'text'}>
                <TextSearchTab formData={formData} handleChange={handleChange} />
              </TabContentAnimated>
            </Tab>
            <Tab
              eventKey="screenshot"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>📷</span> Скриншот
                </span>
              }
            >
              <TabContentAnimated isActive={formData.searchType === 'screenshot'}>
                <ScreenshotTab
                  files={files}
                  handleFilesChange={handleFilesChange}
                  handleRemoveFile={handleRemoveFile}
                  onRemoveAll={() => setFiles([])}
                />
              </TabContentAnimated>
            </Tab>
          </Tabs>
        </div>
      </div>

      <div className="d-grid mt-3">
        <button
          type="submit"
          disabled={isBusy}
          className="glass-btn-primary"
          aria-busy={isBusy}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {isBusy ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                style={{ borderColor: 'currentColor transparent transparent' }}
              />
              Обработка...
            </>
          ) : (
            <>
              <RocketOutlined style={{ fontSize: '16px' }} />
              Создать протокол
            </>
          )}
        </button>
      </div>
    </Form>
  );
};

export default TaskForm;

